// src/pages/AdminCandidatures.js — avec système de blocage
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

  :root {
    --bg:        #0f1117;
    --surface:   #181c27;
    --surface2:  #1e2435;
    --border:    #262d42;
    --accent:    #c9a227;
    --accent-d:  #a07d15;
    --green:     #22c55e;
    --red:       #ef4444;
    --amber:     #f59e0b;
    --orange:    #f97316;
    --muted:     #64748b;
    --text:      #e2e8f0;
    --text2:     #94a3b8;
    --radius:    10px;
    --font:      'DM Sans', sans-serif;
    --mono:      'JetBrains Mono', monospace;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  .ac-wrap {
    min-height: 100vh;
    background: var(--bg);
    color: var(--text);
    font-family: var(--font);
    display: flex;
    flex-direction: column;
  }

  /* TOPBAR */
  .ac-topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 2rem;
    height: 56px;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    position: sticky;
    top: 0;
    z-index: 100;
  }
  .ac-topbar-brand {
    display: flex;
    align-items: center;
    gap: .6rem;
    font-weight: 700;
    font-size: .9rem;
    letter-spacing: .05em;
    text-transform: uppercase;
    color: var(--accent);
  }
  .ac-topbar-brand span { color: var(--text2); font-weight: 400; }
  .ac-topbar-right { display: flex; align-items: center; gap: 1rem; }
  .ac-badge {
    font-size: .65rem;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 20px;
    font-family: var(--mono);
    letter-spacing: .05em;
  }
  .ac-badge.amber { background: var(--amber); color: #000; }
  .ac-badge.orange { background: var(--orange); color: #fff; }
  .ac-refresh-btn {
    background: none;
    border: 1px solid var(--border);
    color: var(--text2);
    padding: 5px 12px;
    border-radius: 6px;
    cursor: pointer;
    font-size: .78rem;
    font-family: var(--font);
    transition: all .15s;
  }
  .ac-refresh-btn:hover { border-color: var(--accent); color: var(--accent); }

  /* STATS */
  .ac-stats {
    display: flex;
    gap: 1px;
    background: var(--border);
    border-bottom: 1px solid var(--border);
  }
  .ac-stat {
    flex: 1;
    padding: 1rem 1.5rem;
    background: var(--surface);
    display: flex;
    flex-direction: column;
    gap: 2px;
    cursor: pointer;
    transition: background .15s;
    position: relative;
  }
  .ac-stat:hover { background: var(--surface2); }
  .ac-stat.active { background: var(--surface2); }
  .ac-stat.active::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 2px;
    background: var(--accent);
  }
  .ac-stat-val {
    font-size: 1.6rem;
    font-weight: 700;
    font-family: var(--mono);
    line-height: 1;
  }
  .ac-stat-val.amber  { color: var(--amber);  }
  .ac-stat-val.green  { color: var(--green);  }
  .ac-stat-val.red    { color: var(--red);    }
  .ac-stat-val.orange { color: var(--orange); }
  .ac-stat-val.white  { color: var(--text);   }
  .ac-stat-label {
    font-size: .72rem;
    color: var(--text2);
    text-transform: uppercase;
    letter-spacing: .08em;
  }

  /* LAYOUT */
  .ac-main {
    display: flex;
    flex: 1;
    overflow: hidden;
    height: calc(100vh - 56px - 73px);
  }

  /* LIST */
  .ac-list-panel {
    flex: 1;
    overflow-y: auto;
    border-right: 1px solid var(--border);
  }
  .ac-list-panel::-webkit-scrollbar { width: 4px; }
  .ac-list-panel::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }

  /* TOOLBAR */
  .ac-toolbar {
    display: flex;
    align-items: center;
    gap: .75rem;
    padding: .75rem 1.25rem;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    position: sticky;
    top: 0;
    z-index: 10;
  }
  .ac-search {
    flex: 1;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 6px 12px;
    font-family: var(--font);
    font-size: .82rem;
    color: var(--text);
    outline: none;
    transition: border .15s;
  }
  .ac-search:focus { border-color: var(--accent); }
  .ac-search::placeholder { color: var(--muted); }
  .ac-filter-btns { display: flex; gap: 4px; }
  .ac-filter-btn {
    padding: 5px 12px;
    border-radius: 5px;
    font-size: .74rem;
    font-family: var(--font);
    font-weight: 500;
    cursor: pointer;
    border: 1px solid var(--border);
    background: none;
    color: var(--text2);
    transition: all .15s;
    white-space: nowrap;
  }
  .ac-filter-btn:hover { border-color: var(--accent); color: var(--accent); }
  .ac-filter-btn.active {
    background: var(--accent);
    border-color: var(--accent);
    color: #000;
    font-weight: 600;
  }

  /* ROWS */
  .ac-row {
    display: grid;
    grid-template-columns: 36px 1fr 160px 90px 70px;
    align-items: center;
    padding: 0 1.25rem;
    height: 52px;
    border-bottom: 1px solid var(--border);
    cursor: pointer;
    transition: background .12s;
    gap: .75rem;
  }
  .ac-row:hover { background: var(--surface2); }
  .ac-row.selected { background: #1e2d4a; border-left: 2px solid var(--accent); }
  .ac-row.bloque { opacity: .65; }
  .ac-row-avatar {
    width: 32px; height: 32px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-d), var(--accent));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: .7rem;
    font-weight: 700;
    color: #000;
    flex-shrink: 0;
  }
  .ac-row-avatar.bloque {
    background: linear-gradient(135deg, #374151, #4b5563);
    color: var(--text2);
  }
  .ac-row-name { min-width: 0; display: flex; flex-direction: column; gap: 1px; }
  .ac-row-name strong {
    font-size: .83rem;
    font-weight: 600;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ac-row-name small {
    font-size: .7rem;
    color: var(--text2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-family: var(--mono);
  }
  .ac-row-specs {
    font-size: .7rem;
    color: var(--text2);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ac-row-date {
    font-size: .7rem;
    color: var(--muted);
    font-family: var(--mono);
    text-align: right;
  }

  /* STATUS PILL */
  .ac-pill {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 8px;
    border-radius: 20px;
    font-size: .65rem;
    font-weight: 600;
    letter-spacing: .04em;
    text-transform: uppercase;
    white-space: nowrap;
  }
  .ac-pill.attente { background: rgba(245,158,11,.15); color: var(--amber);  border: 1px solid rgba(245,158,11,.3); }
  .ac-pill.valide  { background: rgba(34,197,94,.12);  color: var(--green);  border: 1px solid rgba(34,197,94,.3);  }
  .ac-pill.refuse  { background: rgba(239,68,68,.12);  color: var(--red);    border: 1px solid rgba(239,68,68,.3);  }
  .ac-pill.bloque  { background: rgba(249,115,22,.15); color: var(--orange); border: 1px solid rgba(249,115,22,.3); }
  .ac-pill-dot { width: 5px; height: 5px; border-radius: 50%; background: currentColor; }

  /* EMPTY */
  .ac-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    gap: .75rem;
    color: var(--muted);
  }
  .ac-empty-icon { font-size: 2rem; opacity: .4; }
  .ac-empty-text { font-size: .85rem; }

  /* DETAIL PANEL */
  .ac-detail {
    width: 380px;
    flex-shrink: 0;
    background: var(--surface);
    overflow-y: auto;
    display: flex;
    flex-direction: column;
  }
  .ac-detail::-webkit-scrollbar { width: 4px; }
  .ac-detail::-webkit-scrollbar-thumb { background: var(--border); border-radius: 4px; }
  .ac-detail-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    flex: 1;
    gap: 1rem;
    color: var(--muted);
    padding: 2rem;
    text-align: center;
  }
  .ac-detail-empty-icon { font-size: 2.5rem; opacity: .25; }
  .ac-detail-empty-text { font-size: .82rem; line-height: 1.5; }

  .ac-detail-hero {
    padding: 1.5rem;
    border-bottom: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    gap: .75rem;
  }
  .ac-detail-avatar-row { display: flex; align-items: center; gap: 1rem; }
  .ac-detail-avatar {
    width: 52px; height: 52px;
    border-radius: 50%;
    background: linear-gradient(135deg, var(--accent-d), var(--accent));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.1rem;
    font-weight: 700;
    color: #000;
    flex-shrink: 0;
  }
  .ac-detail-avatar.bloque {
    background: linear-gradient(135deg, #374151, #4b5563);
    color: var(--text2);
  }
  .ac-detail-name { font-size: 1rem; font-weight: 700; }
  .ac-detail-email { font-size: .72rem; color: var(--text2); font-family: var(--mono); }
  .ac-detail-meta { display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; }

  .ac-detail-section {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--border);
    display: flex;
    flex-direction: column;
    gap: .6rem;
  }
  .ac-detail-section-title {
    font-size: .68rem;
    text-transform: uppercase;
    letter-spacing: .1em;
    color: var(--muted);
    font-weight: 600;
  }
  .ac-detail-row { display: flex; justify-content: space-between; align-items: baseline; gap: .5rem; }
  .ac-detail-key { font-size: .75rem; color: var(--text2); flex-shrink: 0; }
  .ac-detail-val { font-size: .78rem; font-weight: 500; text-align: right; }
  .ac-tags { display: flex; flex-wrap: wrap; gap: 4px; }
  .ac-tag {
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: 4px;
    padding: 2px 8px;
    font-size: .68rem;
    color: var(--text2);
  }
  .ac-detail-bio {
    font-size: .77rem;
    color: var(--text2);
    line-height: 1.6;
    background: var(--surface2);
    padding: .75rem;
    border-radius: 6px;
    border: 1px solid var(--border);
    max-height: 120px;
    overflow-y: auto;
  }

  /* HISTORIQUE BLOCAGES */
  .ac-blocage-log {
    display: flex;
    flex-direction: column;
    gap: .4rem;
  }
  .ac-blocage-item {
    display: flex;
    align-items: flex-start;
    gap: .6rem;
    padding: .6rem;
    background: var(--surface2);
    border-radius: 6px;
    border: 1px solid var(--border);
  }
  .ac-blocage-icon { font-size: .85rem; flex-shrink: 0; margin-top: 1px; }
  .ac-blocage-info { flex: 1; min-width: 0; }
  .ac-blocage-action {
    font-size: .72rem;
    font-weight: 600;
    color: var(--text);
  }
  .ac-blocage-action.bloque  { color: var(--orange); }
  .ac-blocage-action.debloque { color: var(--green); }
  .ac-blocage-meta {
    font-size: .65rem;
    color: var(--muted);
    font-family: var(--mono);
    margin-top: 1px;
  }
  .ac-blocage-raison {
    font-size: .7rem;
    color: var(--text2);
    margin-top: 2px;
    font-style: italic;
  }

  /* RAISON INPUT */
  .ac-raison-input {
    width: 100%;
    background: var(--bg);
    border: 1px solid var(--border);
    border-radius: 6px;
    padding: 8px 12px;
    font-family: var(--font);
    font-size: .78rem;
    color: var(--text);
    outline: none;
    resize: none;
    transition: border .15s;
    min-height: 60px;
  }
  .ac-raison-input:focus { border-color: var(--orange); }
  .ac-raison-input::placeholder { color: var(--muted); }

  /* ACTIONS */
  .ac-actions {
    padding: 1.25rem 1.5rem;
    display: flex;
    flex-direction: column;
    gap: .6rem;
    margin-top: auto;
    border-top: 1px solid var(--border);
  }
  .ac-actions-row { display: flex; gap: .6rem; }
  .ac-btn {
    flex: 1;
    padding: 10px;
    border-radius: 8px;
    font-family: var(--font);
    font-weight: 600;
    font-size: .82rem;
    cursor: pointer;
    border: none;
    transition: all .15s;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: .4rem;
  }
  .ac-btn:disabled { opacity: .4; cursor: not-allowed; transform: none !important; }
  .ac-btn-validate { background: var(--green); color: #fff; }
  .ac-btn-validate:hover:not(:disabled) { opacity: .85; transform: translateY(-1px); }
  .ac-btn-refuse { background: none; border: 1px solid var(--red); color: var(--red); }
  .ac-btn-refuse:hover:not(:disabled) { background: rgba(239,68,68,.1); transform: translateY(-1px); }
  .ac-btn-block { background: var(--orange); color: #fff; }
  .ac-btn-block:hover:not(:disabled) { opacity: .85; transform: translateY(-1px); }
  .ac-btn-unblock { background: none; border: 1px solid var(--green); color: var(--green); }
  .ac-btn-unblock:hover:not(:disabled) { background: rgba(34,197,94,.1); transform: translateY(-1px); }

  /* BLOC ALERTE */
  .ac-bloque-banner {
    margin: 1rem 1.5rem 0;
    padding: .75rem 1rem;
    background: rgba(249,115,22,.1);
    border: 1px solid rgba(249,115,22,.3);
    border-radius: 8px;
    font-size: .78rem;
    color: var(--orange);
    display: flex;
    align-items: center;
    gap: .5rem;
  }

  /* TOAST */
  .ac-toast {
    position: fixed;
    bottom: 1.5rem;
    right: 1.5rem;
    background: var(--surface2);
    border: 1px solid var(--border);
    padding: .75rem 1.25rem;
    border-radius: 8px;
    font-size: .82rem;
    display: flex;
    align-items: center;
    gap: .5rem;
    box-shadow: 0 8px 24px rgba(0,0,0,.4);
    animation: slideUp .2s ease;
    z-index: 999;
  }
  .ac-toast.success { border-color: var(--green); }
  .ac-toast.error   { border-color: var(--red);   }
  .ac-toast.warning { border-color: var(--orange); }
  @keyframes slideUp {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .ac-loading {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    gap: .75rem;
    color: var(--muted);
    font-size: .85rem;
  }
  .ac-spinner {
    width: 18px; height: 18px;
    border: 2px solid var(--border);
    border-top-color: var(--accent);
    border-radius: 50%;
    animation: spin .6s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

/* ─── HELPERS ───────────────────────────────────────────────────────────── */
const API = process.env.REACT_APP_API_URL || 'https://firstarabic-api.vercel.app';

const getToken = async () => {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token;
};

const initials = (prenom = '', nom = '') =>
  `${prenom[0] || ''}${nom[0] || ''}`.toUpperCase();

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit' });
};

const fmtDatetime = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const StatusPill = ({ s }) => {
  const map = {
    en_attente: ['attente', 'En attente'],
    valide:     ['valide',  'Validé'],
    refuse:     ['refuse',  'Refusé'],
    bloque:     ['bloque',  'Bloqué'],
  };
  const [cls, label] = map[s] || ['attente', s];
  return (
    <span className={`ac-pill ${cls}`}>
      <span className="ac-pill-dot" />
      {label}
    </span>
  );
};

/* ─── COMPONENT ─────────────────────────────────────────────────────────── */
export default function AdminCandidatures() {
  const [cands, setCands]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filtre, setFiltre]     = useState('tous');
  const [search, setSearch]     = useState('');
  const [selected, setSelected] = useState(null);
  const [busy, setBusy]         = useState(false);
  const [toast, setToast]       = useState(null);
  const [raison, setRaison]     = useState('');
  const [historique, setHistorique] = useState([]);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      if (!token) { showToast('Connexion admin requise', 'error'); return; }
      const r = await fetch(`${API}/admin/candidatures`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(r.status);
      const data = await r.json();
      setCands(data.candidatures || data || []);
    } catch (e) {
      showToast(`Erreur chargement: ${e.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Charger l'historique de blocage quand on sélectionne un prof
  const loadHistorique = useCallback(async (profId) => {
    try {
      const token = await getToken();
      const r = await fetch(`${API}/admin/profs/${profId}/blocages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      setHistorique(data.blocages || []);
    } catch {
      setHistorique([]);
    }
  }, []);

  const handleSelect = (c) => {
    setSelected(c);
    setRaison('');
    loadHistorique(c.id);
  };

  /* stats */
  const stats = {
    tous:       cands.length,
    en_attente: cands.filter(c => c.statut_profil === 'en_attente').length,
    valide:     cands.filter(c => c.statut_profil === 'valide').length,
    refuse:     cands.filter(c => c.statut_profil === 'refuse').length,
    bloque:     cands.filter(c => c.statut_profil === 'bloque').length,
  };

  const visible = cands.filter(c => {
    const matchFiltre = filtre === 'tous' || c.statut_profil === filtre;
    const q = search.toLowerCase();
    const matchSearch = !q || (
      `${c.prenom} ${c.nom} ${c.email}`.toLowerCase().includes(q) ||
      (c.specialites || []).join(' ').toLowerCase().includes(q)
    );
    return matchFiltre && matchSearch;
  });

  /* action générique */
  const doAction = async (type) => {
    if (!selected) return;
    const label = `${selected.prenom} ${selected.nom}`;

    const confirmMsgs = {
      valider:   `Valider la candidature de ${label} ?`,
      refuser:   `Refuser la candidature de ${label} ?`,
      bloquer:   `🔒 Bloquer ${label} ?\n\nSes réservations futures seront annulées.`,
      debloquer: `🔓 Débloquer ${label} et rétablir son accès ?`,
    };
    if (!window.confirm(confirmMsgs[type])) return;

    setBusy(true);
    try {
      const token = await getToken();
      const endpoints = {
        valider:   `${API}/admin/candidatures/${selected.id}/valider`,
        refuser:   `${API}/admin/candidatures/${selected.id}/refuser`,
        bloquer:   `${API}/admin/profs/${selected.id}/bloquer`,
        debloquer: `${API}/admin/profs/${selected.id}/debloquer`,
      };

      const r = await fetch(endpoints[type], {
        method: ['valider','refuser'].includes(type) ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ raison }),
      });
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.error || r.status);
      }

      const toastMsgs = {
        valider:   `✅ ${label} validé(e)`,
        refuser:   `❌ ${label} refusé(e)`,
        bloquer:   `🔒 ${label} bloqué(e)`,
        debloquer: `🔓 ${label} débloqué(e)`,
      };
      showToast(toastMsgs[type]);
      setRaison('');
      await load();
      await loadHistorique(selected.id);
    } catch (e) {
      showToast(`Erreur: ${e.message}`, 'error');
    } finally {
      setBusy(false);
    }
  };

  const selCand = cands.find(c => c.id === selected?.id) || selected;
  const isBloque = selCand?.statut_profil === 'bloque';
  const isValide = selCand?.statut_profil === 'valide';
  const isAttente = selCand?.statut_profil === 'en_attente';

  return (
    <>
      <style>{CSS}</style>
      <div className="ac-wrap">

        {/* TOPBAR */}
        <div className="ac-topbar">
          <div className="ac-topbar-brand">
            FirstArabic <span>&nbsp;/ Admin candidatures</span>
          </div>
          <div className="ac-topbar-right">
            {stats.en_attente > 0 && <span className="ac-badge amber">{stats.en_attente} en attente</span>}
            {stats.bloque > 0 && <span className="ac-badge orange">{stats.bloque} bloqué{stats.bloque > 1 ? 's' : ''}</span>}
            <button className="ac-refresh-btn" onClick={load} disabled={loading}>
              {loading ? '…' : '↺ Actualiser'}
            </button>
          </div>
        </div>

        {/* STATS */}
        <div className="ac-stats">
          {[
            { key: 'tous',       label: 'Total',    cls: 'white'  },
            { key: 'en_attente', label: 'Attente',  cls: 'amber'  },
            { key: 'valide',     label: 'Validés',  cls: 'green'  },
            { key: 'bloque',     label: 'Bloqués',  cls: 'orange' },
            { key: 'refuse',     label: 'Refusés',  cls: 'red'    },
          ].map(s => (
            <div
              key={s.key}
              className={`ac-stat ${filtre === s.key ? 'active' : ''}`}
              onClick={() => setFiltre(s.key)}
            >
              <div className={`ac-stat-val ${s.cls}`}>{stats[s.key]}</div>
              <div className="ac-stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* MAIN */}
        <div className="ac-main">

          {/* LIST */}
          <div className="ac-list-panel">
            <div className="ac-toolbar">
              <input
                className="ac-search"
                placeholder="Rechercher nom, email, spécialité…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
              <div className="ac-filter-btns">
                {[
                  { key: 'tous',       label: 'Tous'    },
                  { key: 'en_attente', label: 'Attente' },
                  { key: 'valide',     label: 'Validés' },
                  { key: 'bloque',     label: 'Bloqués' },
                  { key: 'refuse',     label: 'Refusés' },
                ].map(f => (
                  <button
                    key={f.key}
                    className={`ac-filter-btn ${filtre === f.key ? 'active' : ''}`}
                    onClick={() => setFiltre(f.key)}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="ac-loading">
                <div className="ac-spinner" /> Chargement…
              </div>
            ) : visible.length === 0 ? (
              <div className="ac-empty">
                <div className="ac-empty-icon">📋</div>
                <div className="ac-empty-text">Aucune candidature trouvée</div>
              </div>
            ) : (
              visible.map(c => (
                <div
                  key={c.id}
                  className={`ac-row ${selCand?.id === c.id ? 'selected' : ''} ${c.statut_profil === 'bloque' ? 'bloque' : ''}`}
                  onClick={() => handleSelect(c)}
                >
                  <div className={`ac-row-avatar ${c.statut_profil === 'bloque' ? 'bloque' : ''}`}>
                    {c.statut_profil === 'bloque' ? '🔒' : initials(c.prenom, c.nom)}
                  </div>
                  <div className="ac-row-name">
                    <strong>{c.prenom} {c.nom}</strong>
                    <small>{c.email}</small>
                  </div>
                  <div className="ac-row-specs">
                    {(c.specialites || []).slice(0, 2).join(', ') || '—'}
                    {(c.specialites || []).length > 2 && ` +${c.specialites.length - 2}`}
                  </div>
                  <StatusPill s={c.statut_profil} />
                  <div className="ac-row-date">{fmtDate(c.created_at)}</div>
                </div>
              ))
            )}
          </div>

          {/* DETAIL */}
          <div className="ac-detail">
            {!selCand ? (
              <div className="ac-detail-empty">
                <div className="ac-detail-empty-icon">👆</div>
                <div className="ac-detail-empty-text">
                  Sélectionnez une candidature<br />pour voir le détail
                </div>
              </div>
            ) : (
              <>
                {/* HERO */}
                <div className="ac-detail-hero">
                  <div className="ac-detail-avatar-row">
                    <div className={`ac-detail-avatar ${isBloque ? 'bloque' : ''}`}>
                      {isBloque ? '🔒' : initials(selCand.prenom, selCand.nom)}
                    </div>
                    <div>
                      <div className="ac-detail-name">{selCand.prenom} {selCand.nom}</div>
                      <div className="ac-detail-email">{selCand.email}</div>
                    </div>
                  </div>
                  <div className="ac-detail-meta">
                    <StatusPill s={selCand.statut_profil} />
                    <span className="ac-tag">📅 {fmtDate(selCand.created_at)}</span>
                    {selCand.pays_origine && <span className="ac-tag">🌍 {selCand.pays_origine}</span>}
                    {selCand.experience_annees && <span className="ac-tag">⏱ {selCand.experience_annees} ans</span>}
                  </div>
                </div>

                {/* BANNIÈRE BLOQUÉ */}
                {isBloque && (
                  <div className="ac-bloque-banner">
                    🔒 Ce professeur est bloqué — son accès est suspendu
                  </div>
                )}

                {/* TARIFS */}
                <div className="ac-detail-section">
                  <div className="ac-detail-section-title">Tarifs</div>
                  <div className="ac-detail-row">
                    <span className="ac-detail-key">30 min</span>
                    <span className="ac-detail-val">{selCand.prix_30min ?? '—'} €</span>
                  </div>
                  <div className="ac-detail-row">
                    <span className="ac-detail-key">60 min</span>
                    <span className="ac-detail-val">{selCand.prix_60min ?? '—'} €</span>
                  </div>
                </div>

                {/* SPÉCIALISATIONS */}
                {(selCand.specialites || []).length > 0 && (
                  <div className="ac-detail-section">
                    <div className="ac-detail-section-title">Spécialisations</div>
                    <div className="ac-tags">
                      {selCand.specialites.map((s, i) => <span key={i} className="ac-tag">{s}</span>)}
                    </div>
                  </div>
                )}

                {/* LANGUES */}
                {(selCand.langues_communication || []).length > 0 && (
                  <div className="ac-detail-section">
                    <div className="ac-detail-section-title">Langues d'enseignement</div>
                    <div className="ac-tags">
                      {selCand.langues_communication.map((l, i) => <span key={i} className="ac-tag">🗣 {l}</span>)}
                    </div>
                  </div>
                )}

                {/* BIO */}
                {selCand.bio && (
                  <div className="ac-detail-section">
                    <div className="ac-detail-section-title">Biographie</div>
                    <div className="ac-detail-bio">{selCand.bio}</div>
                  </div>
                )}

                {/* HISTORIQUE BLOCAGES */}
                {historique.length > 0 && (
                  <div className="ac-detail-section">
                    <div className="ac-detail-section-title">Historique blocages</div>
                    <div className="ac-blocage-log">
                      {historique.map((h) => (
                        <div key={h.id} className="ac-blocage-item">
                          <div className="ac-blocage-icon">{h.action === 'bloque' ? '🔒' : '🔓'}</div>
                          <div className="ac-blocage-info">
                            <div className={`ac-blocage-action ${h.action}`}>
                              {h.action === 'bloque' ? 'Bloqué' : 'Débloqué'}
                            </div>
                            <div className="ac-blocage-meta">
                              {fmtDatetime(h.created_at)} · {h.admin_email}
                            </div>
                            {h.raison && <div className="ac-blocage-raison">"{h.raison}"</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* ACTIONS */}
                <div className="ac-actions">

                  {/* Raison (pour blocage/déblocage) */}
                  {(isValide || isBloque) && (
                    <textarea
                      className="ac-raison-input"
                      placeholder={isValide ? "Raison du blocage (optionnel)…" : "Raison du déblocage (optionnel)…"}
                      value={raison}
                      onChange={e => setRaison(e.target.value)}
                      rows={2}
                    />
                  )}

                  {/* Candidature en attente → Valider / Refuser */}
                  {isAttente && (
                    <div className="ac-actions-row">
                      <button className="ac-btn ac-btn-validate" onClick={() => doAction('valider')} disabled={busy}>
                        {busy ? '…' : '✓ Valider'}
                      </button>
                      <button className="ac-btn ac-btn-refuse" onClick={() => doAction('refuser')} disabled={busy}>
                        {busy ? '…' : '✕ Refuser'}
                      </button>
                    </div>
                  )}

                  {/* Prof validé → Bloquer */}
                  {isValide && (
                    <button className="ac-btn ac-btn-block" onClick={() => doAction('bloquer')} disabled={busy}>
                      {busy ? '…' : '🔒 Bloquer ce professeur'}
                    </button>
                  )}

                  {/* Prof bloqué → Débloquer */}
                  {isBloque && (
                    <button className="ac-btn ac-btn-unblock" onClick={() => doAction('debloquer')} disabled={busy}>
                      {busy ? '…' : '🔓 Débloquer'}
                    </button>
                  )}

                </div>
              </>
            )}
          </div>
        </div>

        {/* TOAST */}
        {toast && <div className={`ac-toast ${toast.type}`}>{toast.msg}</div>}
      </div>
    </>
  );
}
